import { useLayoutEffect } from "react"
import { Outlet, useLocation } from "react-router"
import { Wordmark } from "@/brand/Wordmark"
import { removeBootScreen } from "@/lib/boot-screen"
import { useConfig } from "@/auth/mode"
import { AuthLanguageSwitch } from "./AuthLanguageSwitch"
import { AuthFeatureChips, AuthShowcase } from "./AuthShowcase"
import { AuthFooter } from "./SiteFooter"

/**
 * The wrapper for every unauthenticated screen: sign-in, password reset,
 * invitation acceptance, email verification. The form on a lit, dotted
 * ground; on wide screens, the brand panel beside it (`AuthShowcase`). The
 * looks are static CSS (src/theme/auth.css), so index.html paints them too.
 *
 * index.html's static first paint is the SIGN-IN screen. On `/sign-in` the
 * layout leaves it up and the screen replaces it when it renders (`AuthCard`),
 * so the heading is painted once, not twice (a second paint became the LCP,
 * NFR-DSH-01). On any other auth route (a cold visit from an email link to
 * `/verify-email`, `/accept-invite`, `/reset-password`) it is the wrong
 * screen, so the layout removes it at once, before that route's chunk has
 * loaded.
 *
 * Top-aligned rather than centred: the form below a screen's heading arrives
 * after `getConfig`, and a centred column would move the heading (the largest
 * paint, painted first by index.html) when it does (NFR-DSH-01, no layout
 * shift).
 */
/** The page behind the column, past its bottom edge too (a long form, a full-page capture). */
const PAGE_GROUND = "bg-surface-sunken"

export function AuthLayout() {
  const { pathname } = useLocation()
  const { data: config } = useConfig()
  useLayoutEffect(() => {
    if (pathname !== "/sign-in") removeBootScreen()
  }, [pathname])
  useLayoutEffect(() => {
    document.body.classList.add(PAGE_GROUND)
    return () => {
      document.body.classList.remove(PAGE_GROUND)
    }
  }, [])
  return (
    <div className="lg:grid lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
      <AuthShowcase />
      <div className="auth-ground relative isolate flex min-h-dvh flex-col items-center px-4 pt-14 pb-12 sm:pt-20">
        <div aria-hidden="true" className="auth-dots absolute inset-0 -z-10" />
        <div className="absolute top-2 right-2">
          <AuthLanguageSwitch />
        </div>
        <Wordmark
          size="lg"
          className="mb-8 text-lg font-semibold tracking-tight text-foreground lg:hidden"
        />
        <main className="w-full max-w-sm lg:mt-12">
          <Outlet />
        </main>
        <AuthFeatureChips />
        <AuthFooter supportEmail={config?.brand.supportEmail} />
      </div>
    </div>
  )
}
